import { Booking, Resource, WorkingHoursProfile, Absence, BankHoliday, BookingType } from '@/types';
import { startOfDay, endOfDay, getDay, isSameDay, parseISO } from 'date-fns';

/**
 * Get the day key for WorkingHoursProfile based on a date
 */
function getDayKey(date: Date): keyof WorkingHoursProfile {
  const dayOfWeek = getDay(date); // 0 = Sunday, 1 = Monday, etc.
  const dayKeys: (keyof WorkingHoursProfile)[] = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'
  ];
  return dayKeys[dayOfWeek];
}

export function isWorkingHoursProfileConfigured(
  profile: WorkingHoursProfile | undefined,
): profile is WorkingHoursProfile {
  return Boolean(profile && Object.keys(profile).length > 0);
}

/**
 * Normalise a durationType value from any source (frontend form, API, database)
 * into one of the canonical frontend categories: 'full-day', 'half-day',
 * 'half-day-am', 'half-day-pm', or 'hourly'.
 *
 * The database enum uses 'hours' / 'days' / 'weeks' while the frontend
 * type uses 'full-day' / 'half-day' / 'hourly'.  The API may also return
 * null, undefined, or PascalCase variants.
 */
export function normalizeDurationType(
  raw: string | null | undefined,
  halfDayPeriod?: string | null,
): 'full-day' | 'half-day' | 'half-day-am' | 'half-day-pm' | 'hourly' {
  const dt = (raw || '').toLowerCase().trim();
  const period = (halfDayPeriod || '').toLowerCase().trim();
  if (dt === 'full-day' || dt === 'fullday' || dt === 'full day' || dt === 'full_day' || dt === 'days' || dt === 'weeks') {
    return 'full-day';
  }
  if (dt === 'half-day-am' || dt === 'halfday-am' || dt === 'half-day am' || dt === 'morning' || dt === 'am') return 'half-day-am';
  if (dt === 'half-day-pm' || dt === 'halfday-pm' || dt === 'half-day pm' || dt === 'afternoon' || dt === 'pm') return 'half-day-pm';
  if (dt === 'half-day' || dt === 'halfday' || dt === 'half day') {
    if (period === 'am' || period === 'morning') return 'half-day-am';
    if (period === 'pm' || period === 'afternoon') return 'half-day-pm';
    return 'half-day';
  }
  if (dt === 'hourly' || dt === 'hours') return 'hourly';
  if (period === 'am' || period === 'morning') return 'half-day-am';
  if (period === 'pm' || period === 'afternoon') return 'half-day-pm';
  // 'hourly', 'hours', or any unrecognised value → time-based calculation
  return 'hourly';
}

/**
 * Get working hours for a resource on a specific day
 * Uses custom profile if enabled, otherwise falls back to global profile
 */
export function getWorkingHoursForDay(
  resource: Resource,
  date: Date,
  globalWorkingProfile: WorkingHoursProfile
): number {
  const dayKey = getDayKey(date);
  
  // Prefer custom profile whenever it exists (non-empty)
  if (isWorkingHoursProfileConfigured(resource.customWorkingProfile)) {
    return resource.customWorkingProfile[dayKey] || 0;
  }
  
  // Otherwise use global working profile
  return globalWorkingProfile[dayKey] || 0;
}

/**
 * Total hours for an hourly booking, capped per calendar day at the working
 * profile hours for that day. Days with zero profile hours contribute nothing.
 */
export function computeCappedHourlyTotal(
  startTime: Date,
  endTime: Date,
  resource: Resource | undefined,
  globalWorkingProfile: WorkingHoursProfile | undefined
): number {
  if (!startTime || !endTime || endTime.getTime() <= startTime.getTime()) return 0;

  const rawHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
  if (!resource || !globalWorkingProfile) return rawHours;

  let total = 0;
  const day = startOfDay(startTime);
  while (day.getTime() < endTime.getTime()) {
    const nextDay = new Date(day);
    nextDay.setDate(nextDay.getDate() + 1);

    const overlapStart = Math.max(day.getTime(), startTime.getTime());
    const overlapEnd = Math.min(nextDay.getTime(), endTime.getTime());
    const overlapHours = (overlapEnd - overlapStart) / (1000 * 60 * 60);

    if (overlapHours > 0) {
      const profileHours = getWorkingHoursForDay(resource, day, globalWorkingProfile);
      total += Math.min(overlapHours, profileHours);
    }

    day.setTime(nextDay.getTime());
  }

  return total;
}

/**
 * Calculate absence minutes for a resource on a specific day.
 * The result is clamped to the day's working minutes so a long or malformed
 * absence can never remove more capacity than the day actually has.
 */
export function calculateAbsenceMinutes(
  resourceId: string,
  date: Date,
  absences: Absence[],
  workingHoursPerDay: number
): number {
  const dayStart = startOfDay(date);
  const dayEnd = endOfDay(date);
  const workingMinutes = workingHoursPerDay * 60;
  let absenceMinutes = 0;

  // Filter absences for this resource on this day
  const dayAbsences = absences.filter((absence) => {
    const absenceStart = parseISO(absence.startDate);
    const absenceEnd = parseISO(absence.endDate);
    
    return (
      String(absence.resourceId) === String(resourceId) &&
      absenceStart <= dayEnd &&
      absenceEnd >= dayStart
    );
  });

  dayAbsences.forEach((absence) => {
    switch (normalizeDurationType(absence.type)) {
      case 'full-day':
        // Full-day absence uses all working hours
        absenceMinutes += workingMinutes;
        break;
      case 'half-day':
      case 'half-day-am':
      case 'half-day-pm':
        // Half-day absence uses half of working hours
        absenceMinutes += workingMinutes / 2;
        break;
      case 'hourly':
        // Hourly absence - calculate actual duration
        if (absence.startTime && absence.endTime) {
          const [startHour, startMin] = absence.startTime.split(':').map(Number);
          const [endHour, endMin] = absence.endTime.split(':').map(Number);
          const startMinutes = startHour * 60 + startMin;
          const endMinutes = endHour * 60 + endMin;
          absenceMinutes += Math.max(0, endMinutes - startMinutes);
        }
        break;
    }
  });

  return Math.min(absenceMinutes, workingMinutes);
}

/**
 * Check if a date is a bank holiday
 */
export function isBankHoliday(date: Date, bankHolidays: BankHoliday[]): boolean {
  return bankHolidays.some((holiday) => {
    const holidayDate = parseISO(holiday.date);
    return isSameDay(date, holidayDate);
  });
}

/**
 * Effective end of a booking.
 *
 * A single-day, day-based booking is stored with the same timestamp for start
 * and end, so it runs to the start of the following day.  An hourly booking
 * falls back to its recorded durationHours and otherwise keeps a zero-length
 * span, because a genuine zero-hour booking must consume no capacity.
 */
export function resolveBookingEnd(booking: Booking, isDayBased: boolean): Date {
  const start = new Date(booking.startTime);
  const end = new Date(booking.endTime);
  if (end.getTime() > start.getTime()) return end;

  if (isDayBased) {
    const dayAfter = startOfDay(start);
    dayAfter.setDate(dayAfter.getDate() + 1);
    return dayAfter;
  }
  if (booking.durationHours && booking.durationHours > 0) {
    return new Date(start.getTime() + booking.durationHours * 60 * 60 * 1000);
  }
  return new Date(start);
}

/**
 * Booked minutes for a resource on a single day.
 * Excludes cancelled bookings and supports multi-resource bookings.
 */
export function calculateBookedMinutesForDay(
  resourceId: string,
  date: Date,
  bookings: Booking[],
  workingHoursForDay: number
): number {
  const dayStart = startOfDay(date);
  const dayEnd = endOfDay(date);
  const rid = String(resourceId);

  const dayBookings = bookings.filter((booking) => {
    if (booking.status === 'cancelled') return false;

    const matchesPrimary = booking.resourceId != null && String(booking.resourceId) === rid;
    const matchesMulti = !matchesPrimary && Array.isArray(booking.resourceIds) &&
      booking.resourceIds.some(id => String(id) === rid);
    if (!matchesPrimary && !matchesMulti) return false;

    const dt = normalizeDurationType(booking.durationType, booking.halfDayPeriod);
    const bookingStart = new Date(booking.startTime);
    const bookingEnd = resolveBookingEnd(booking, dt !== 'hourly');
    return bookingStart < dayEnd && bookingEnd > dayStart;
  });

  let bookedMinutes = 0;

  dayBookings.forEach((booking) => {
    const dt = normalizeDurationType(booking.durationType, booking.halfDayPeriod);

    if (dt === 'full-day') {
      bookedMinutes += workingHoursForDay * 60;
    } else if (dt === 'half-day' || dt === 'half-day-am' || dt === 'half-day-pm') {
      bookedMinutes += (workingHoursForDay * 60) / 2;
    } else {
      // Hourly/time-based — clamp to day boundaries
      const bookingStart = new Date(booking.startTime);
      const bookingEnd = resolveBookingEnd(booking, false);
      const effectiveStart = bookingStart < dayStart ? dayStart : bookingStart;
      const effectiveEnd = bookingEnd > dayEnd ? dayEnd : bookingEnd;
      const minutes = Math.max(0, (effectiveEnd.getTime() - effectiveStart.getTime()) / (1000 * 60));
      if (minutes > 0) {
        bookedMinutes += minutes;
      } else if (booking.durationHours && booking.durationHours > 0) {
        // Zero effective span but a real recorded hourly duration — use it.
        bookedMinutes += booking.durationHours * 60;
      }
      // Otherwise this is a genuine zero-hour hourly booking (start == end and
      // no durationHours) — it contributes nothing.
    }
  });

  return bookedMinutes;
}

/**
 * A booking obeys the working profile and bank holidays unless its booking
 * type has the "Obey User Working Profile and Bank Holidays" setting turned
 * off (see BookingTypeSetup).  Non-obeying bookings can legitimately sit on a
 * bank holiday or absence day, so they restore that day's capacity instead of
 * being capped away.
 */
export function bookingObeysWorkingProfile(
  booking: Booking,
  bookingTypes?: BookingType[]
): boolean {
  if (!bookingTypes || booking.bookingTypeId == null) return true;
  const bookingType = bookingTypes.find(bt => String(bt.id) === String(booking.bookingTypeId));
  return bookingType ? bookingType.obeyUserWorkingProfileAndBankHolidays !== false : true;
}

export interface DayCapacity {
  /** Working-profile minutes for the day, before any reductions */
  workingMinutes: number;
  /** Minutes lost to bank holidays and absences */
  lostMinutes: number;
  /** Capacity after bank-holiday and absence reductions */
  availableMinutes: number;
  /** Booked minutes, capped at availableMinutes */
  bookedMinutes: number;
  /** Booked minutes before capping */
  rawBookedMinutes: number;
  isBankHoliday: boolean;
}

/**
 * Canonical single-day capacity calculation shared by every utilisation
 * code path.
 *
 * Bank holidays and absences REDUCE available capacity (the denominator);
 * they are never counted as booked time.  A bank holiday removes the day's
 * hours entirely, a full-day absence removes them entirely, half-day
 * absences remove half and hourly absences remove their own duration.
 * Booked minutes are capped at the reduced capacity so a booking on a
 * partly-absent day can never exceed 100%.
 */
export function calculateDayCapacity(
  resourceId: string,
  date: Date,
  bookings: Booking[],
  workingHoursForDay: number,
  absences?: Absence[],
  bankHolidays?: BankHoliday[],
  bookingTypes?: BookingType[]
): DayCapacity {
  const workingMinutes = Math.max(0, workingHoursForDay * 60);
  const dayIsBankHoliday = Boolean(bankHolidays && isBankHoliday(date, bankHolidays));

  const absenceMinutes = !dayIsBankHoliday && absences
    ? calculateAbsenceMinutes(resourceId, date, absences, workingHoursForDay)
    : 0;

  const lostMinutes = dayIsBankHoliday ? workingMinutes : absenceMinutes;

  const rawBookedMinutes = workingMinutes > 0
    ? calculateBookedMinutesForDay(resourceId, date, bookings, workingHoursForDay)
    : 0;

  // Bookings whose type ignores the working profile / bank holidays keep their
  // capacity on an otherwise-removed day.
  const nonObeyingMinutes = workingMinutes > 0 && lostMinutes > 0 && bookingTypes
    ? calculateBookedMinutesForDay(
        resourceId,
        date,
        bookings.filter(b => !bookingObeysWorkingProfile(b, bookingTypes)),
        workingHoursForDay
      )
    : 0;

  const availableMinutes = Math.min(
    workingMinutes,
    Math.max(0, workingMinutes - lostMinutes, nonObeyingMinutes)
  );

  return {
    workingMinutes,
    lostMinutes: workingMinutes - availableMinutes,
    availableMinutes,
    bookedMinutes: Math.min(rawBookedMinutes, availableMinutes),
    rawBookedMinutes,
    isBankHoliday: dayIsBankHoliday,
  };
}

/**
 * Calculate resource utilization percentage for a specific day.
 * Bank holidays and absences reduce the day's available hours rather than
 * adding to booked hours — see calculateDayCapacity.
 */
export function calculateDailyUtilization(
  resourceId: string,
  date: Date,
  bookings: Booking[],
  resource?: Resource,
  globalWorkingProfile?: WorkingHoursProfile,
  absences?: Absence[],
  bankHolidays?: BankHoliday[],
  bookingTypes?: BookingType[]
): number {
  const workingHoursPerDay = resource && globalWorkingProfile
    ? getWorkingHoursForDay(resource, date, globalWorkingProfile)
    : 8; // Default fallback

  const { availableMinutes, bookedMinutes } = calculateDayCapacity(
    resourceId,
    date,
    bookings,
    workingHoursPerDay,
    absences,
    bankHolidays,
    bookingTypes
  );

  if (availableMinutes === 0 || bookedMinutes === 0) return 0;

  const utilization = (bookedMinutes / availableMinutes) * 100;

  return Math.round(Math.min(utilization, 999)); // Cap at 999% for display
}


/**
 * Calculate utilization for all resources on a specific date
 * Now supports working profiles (global and custom)
 */
export function calculateDateUtilization(
  date: Date,
  resources: Resource[],
  bookings: Booking[],
  globalWorkingProfile?: WorkingHoursProfile
): Map<string, number> {
  const utilizationMap = new Map<string, number>();
  
  resources.forEach((resource) => {
    const utilization = calculateDailyUtilization(
      resource.id, 
      date, 
      bookings, 
      resource, 
      globalWorkingProfile
    );
    utilizationMap.set(resource.id, utilization);
  });
  
  return utilizationMap;
}

/**
 * Calculate average utilization across all resources for a date
 * Now supports working profiles (global and custom)
 */
export function calculateAverageUtilization(
  date: Date,
  resources: Resource[],
  bookings: Booking[],
  globalWorkingProfile?: WorkingHoursProfile
): number {
  if (resources.length === 0) return 0;
  
  const utilizationMap = calculateDateUtilization(date, resources, bookings, globalWorkingProfile);
  const total = Array.from(utilizationMap.values()).reduce((sum, util) => sum + util, 0);
  
  return Math.round(total / resources.length);
}

/**
 * @deprecated Use calculatePeriodUtilization instead — it sums total
 * booked hours / total capacity hours across the period, which is
 * mathematically correct.  These weekly/monthly averages of daily
 * percentages have been removed.
 */


/**
 * Calculate total booked hours for a resource on a specific day
 * Excludes only cancelled bookings
 */
export function calculateTotalBookedHours(
  resourceId: string,
  date: Date,
  bookings: Booking[]
): number {
  const dayStart = startOfDay(date);
  const dayEnd = endOfDay(date);

  // Filter bookings for this resource on this day
  // Exclude only cancelled bookings — planned bookings represent allocated
  // resource time and must count towards utilisation.
  const rid = String(resourceId);
  const dayBookings = bookings.filter((booking) => {
    const bookingStart = new Date(booking.startTime);
    const bookingEnd = new Date(booking.endTime);
    
    if (booking.status === 'cancelled') {
      return false;
    }

    // Match on primary resourceId OR within the resourceIds array
    const matchesPrimary = booking.resourceId != null && String(booking.resourceId) === rid;
    const matchesMulti = !matchesPrimary && Array.isArray(booking.resourceIds) &&
      booking.resourceIds.some(id => String(id) === rid);
    if (!matchesPrimary && !matchesMulti) return false;
    
    return (
      bookingStart < dayEnd &&
      bookingEnd > dayStart
    );
  });

  if (dayBookings.length === 0) return 0;

  // Calculate total booked hours for this day
  let totalBookedMinutes = 0;
  
  dayBookings.forEach((booking) => {
    const bookingStart = new Date(booking.startTime);
    const bookingEnd = new Date(booking.endTime);
    const dt = normalizeDurationType(booking.durationType);

    if (dt === 'full-day') {
      totalBookedMinutes += 8 * 60;
    } else if (dt === 'half-day' || dt === 'half-day-am' || dt === 'half-day-pm') {
      totalBookedMinutes += 4 * 60;
    } else {
      const effectiveStart = bookingStart < dayStart ? dayStart : bookingStart;
      const effectiveEnd = bookingEnd > dayEnd ? dayEnd : bookingEnd;
      const minutes = Math.max(0, (effectiveEnd.getTime() - effectiveStart.getTime()) / (1000 * 60));
      if (minutes > 0) {
        totalBookedMinutes += minutes;
      } else if (booking.durationHours && booking.durationHours > 0) {
        // Zero effective span but a real recorded hourly duration — use it.
        totalBookedMinutes += booking.durationHours * 60;
      }
      // Otherwise a genuine zero-hour hourly booking contributes nothing.
    }
  });

  // Return total hours (rounded to 1 decimal place)
  return Math.round((totalBookedMinutes / 60) * 10) / 10;
}

/**
 * Calculate total booked hours for a resource across a week
 * Excludes cancelled bookings
 */
export function calculateWeeklyBookedHours(
  resourceId: string,
  weekStart: Date,
  weekEnd: Date,
  bookings: Booking[]
): number {
  const days: Date[] = [];
  const currentDate = new Date(weekStart);
  
  // Generate all days in the week
  while (currentDate <= weekEnd) {
    days.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  // Sum up total booked hours across all days
  const totalHours = days.reduce((sum, day) => {
    return sum + calculateTotalBookedHours(resourceId, day, bookings);
  }, 0);
  
  return Math.round(totalHours * 10) / 10;
}

/**
 * Calculate total booked hours for a resource across a month
 * Excludes cancelled bookings
 */
export function calculateMonthlyBookedHours(
  resourceId: string,
  monthStart: Date,
  monthEnd: Date,
  bookings: Booking[]
): number {
  const days: Date[] = [];
  const currentDate = new Date(monthStart);
  
  // Generate all days in the month
  while (currentDate <= monthEnd) {
    days.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  // Sum up total booked hours across all days
  const totalHours = days.reduce((sum, day) => {
    return sum + calculateTotalBookedHours(resourceId, day, bookings);
  }, 0);
  
  return Math.round(totalHours * 10) / 10;
}

/**
 * Calculate available hours for a resource on a specific day based on working profile
 */
export function calculateAvailableHours(
  resource: Resource,
  date: Date,
  globalWorkingProfile: WorkingHoursProfile
): number {
  return getWorkingHoursForDay(resource, date, globalWorkingProfile);
}

/**
 * Calculate available hours for a resource across a week based on working profile
 */
export function calculateWeeklyAvailableHours(
  resource: Resource,
  weekStart: Date,
  weekEnd: Date,
  globalWorkingProfile: WorkingHoursProfile
): number {
  const days: Date[] = [];
  const currentDate = new Date(weekStart);
  
  // Generate all days in the week
  while (currentDate <= weekEnd) {
    days.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  // Sum up available hours across all days
  const totalHours = days.reduce((sum, day) => {
    return sum + getWorkingHoursForDay(resource, day, globalWorkingProfile);
  }, 0);
  
  return Math.round(totalHours * 10) / 10;
}

/**
 * Calculate available hours for a resource across a month based on working profile
 */
export function calculateMonthlyAvailableHours(
  resource: Resource,
  monthStart: Date,
  monthEnd: Date,
  globalWorkingProfile: WorkingHoursProfile
): number {
  const days: Date[] = [];
  const currentDate = new Date(monthStart);
  
  // Generate all days in the month
  while (currentDate <= monthEnd) {
    days.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  // Sum up available hours across all days
  const totalHours = days.reduce((sum, day) => {
    return sum + getWorkingHoursForDay(resource, day, globalWorkingProfile);
  }, 0);
  
  return Math.round(totalHours * 10) / 10;
}

/**
 * Calculate remaining hours for a resource on a specific day
 * Remaining = Available - Booked
 */
export function calculateRemainingHours(
  resourceId: string,
  date: Date,
  bookings: Booking[],
  resource: Resource,
  globalWorkingProfile: WorkingHoursProfile
): number {
  const availableHours = calculateAvailableHours(resource, date, globalWorkingProfile);
  const bookedHours = calculateTotalBookedHours(resourceId, date, bookings);
  const remaining = availableHours - bookedHours;
  
  return Math.round(remaining * 10) / 10;
}

/**
 * Calculate remaining hours for a resource across a week
 * Remaining = Available - Booked
 */
export function calculateWeeklyRemainingHours(
  resourceId: string,
  weekStart: Date,
  weekEnd: Date,
  bookings: Booking[],
  resource: Resource,
  globalWorkingProfile: WorkingHoursProfile
): number {
  const availableHours = calculateWeeklyAvailableHours(resource, weekStart, weekEnd, globalWorkingProfile);
  const bookedHours = calculateWeeklyBookedHours(resourceId, weekStart, weekEnd, bookings);
  const remaining = availableHours - bookedHours;
  
  return Math.round(remaining * 10) / 10;
}

/**
 * Calculate remaining hours for a resource across a month
 * Remaining = Available - Booked
 */
export function calculateMonthlyRemainingHours(
  resourceId: string,
  monthStart: Date,
  monthEnd: Date,
  bookings: Booking[],
  resource: Resource,
  globalWorkingProfile: WorkingHoursProfile
): number {
  const availableHours = calculateMonthlyAvailableHours(resource, monthStart, monthEnd, globalWorkingProfile);
  const bookedHours = calculateMonthlyBookedHours(resourceId, monthStart, monthEnd, bookings);
  const remaining = availableHours - bookedHours;
  
  return Math.round(remaining * 10) / 10;
}

/**
 * Format hours display with abbreviation
 * Examples: "8h", "24h", "160h", "Bkd: 8h", "Avail: 40h"
 */
export function formatHoursDisplay(hours: number, label?: 'Bkd' | 'Avail' | 'Rem'): string {
  const hoursStr = hours % 1 === 0 ? hours.toString() : hours.toFixed(1);
  
  if (label) {
    return `${label}: ${hoursStr}h`;
  }
  
  return `${hoursStr}h`;
}

/**
 * Get capacity metrics for a resource on a specific period
 * Returns available, booked, and remaining hours
 */
export interface CapacityMetrics {
  available: number;
  booked: number;
  remaining: number;
  utilization: number;
}

export function getCapacityMetrics(
  resourceId: string,
  date: Date,
  bookings: Booking[],
  resource: Resource,
  globalWorkingProfile: WorkingHoursProfile
): CapacityMetrics {
  const available = calculateAvailableHours(resource, date, globalWorkingProfile);
  const booked = calculateTotalBookedHours(resourceId, date, bookings);
  const remaining = available - booked;
  const utilization = available > 0 ? Math.round((booked / available) * 100) : 0;
  
  return {
    available: Math.round(available * 10) / 10,
    booked: Math.round(booked * 10) / 10,
    remaining: Math.round(remaining * 10) / 10,
    utilization
  };
}

export function getWeeklyCapacityMetrics(
  resourceId: string,
  weekStart: Date,
  weekEnd: Date,
  bookings: Booking[],
  resource: Resource,
  globalWorkingProfile: WorkingHoursProfile
): CapacityMetrics {
  const available = calculateWeeklyAvailableHours(resource, weekStart, weekEnd, globalWorkingProfile);
  const booked = calculateWeeklyBookedHours(resourceId, weekStart, weekEnd, bookings);
  const remaining = available - booked;
  const utilization = available > 0 ? Math.round((booked / available) * 100) : 0;
  
  return {
    available: Math.round(available * 10) / 10,
    booked: Math.round(booked * 10) / 10,
    remaining: Math.round(remaining * 10) / 10,
    utilization
  };
}

export function getMonthlyCapacityMetrics(
  resourceId: string,
  monthStart: Date,
  monthEnd: Date,
  bookings: Booking[],
  resource: Resource,
  globalWorkingProfile: WorkingHoursProfile
): CapacityMetrics {
  const available = calculateMonthlyAvailableHours(resource, monthStart, monthEnd, globalWorkingProfile);
  const booked = calculateMonthlyBookedHours(resourceId, monthStart, monthEnd, bookings);
  const remaining = available - booked;
  const utilization = available > 0 ? Math.round((booked / available) * 100) : 0;
  
  return {
    available: Math.round(available * 10) / 10,
    booked: Math.round(booked * 10) / 10,
    remaining: Math.round(remaining * 10) / 10,
    utilization
  };
}

export interface PeriodUtilizationResult {
  totalBookedHours: number;
  totalCapacityHours: number;
  utilizationPercentage: number;
}

export interface PeriodCapacityResult extends PeriodUtilizationResult {
  /** Working-profile hours before bank-holiday / absence reductions */
  totalWorkingHours: number;
  /** Hours removed from capacity by bank holidays and absences */
  totalLostHours: number;
}

export interface PeriodCapacityOptions {
  /**
   * Count every booked minute, including minutes that exceed the day's
   * available capacity.  With the default (false) each day is capped at its
   * own capacity, so a resource holding several overlapping bookings on the
   * same day never reports more than 100%.  Callers that need to surface
   * over-booking (e.g. the booking form's resource picker) opt in here.
   */
  includeOverbooking?: boolean;
}

/**
 * Canonical period capacity calculation — the single source of truth for
 * utilisation across the app.
 *
 *   Utilisation % = Booked Hours / Available Hours × 100
 *
 * Available Hours are the working-profile hours for every day in the period
 * MINUS the hours lost to bank holidays and absences.  Bank-holiday and
 * absence hours are never added to Booked Hours.
 */
export function calculatePeriodCapacity(
  resourceId: string,
  periodStart: Date,
  periodEnd: Date,
  bookings: Booking[],
  resource: Resource,
  globalWorkingProfile: WorkingHoursProfile,
  bankHolidays?: BankHoliday[],
  absences?: Absence[],
  bookingTypes?: BookingType[],
  options?: PeriodCapacityOptions
): PeriodCapacityResult {
  const days: Date[] = [];
  const currentDate = new Date(startOfDay(periodStart));
  const end = startOfDay(periodEnd);

  while (currentDate <= end) {
    days.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  let totalBookedMinutes = 0;
  let totalCapacityMinutes = 0;
  let totalWorkingMinutes = 0;
  let totalLostMinutes = 0;

  days.forEach(day => {
    const workingHoursForDay = getWorkingHoursForDay(resource, day, globalWorkingProfile);
    if (workingHoursForDay === 0) return;

    const dayCapacity = calculateDayCapacity(
      resourceId,
      day,
      bookings,
      workingHoursForDay,
      absences,
      bankHolidays,
      bookingTypes
    );

    totalWorkingMinutes += dayCapacity.workingMinutes;
    totalLostMinutes += dayCapacity.lostMinutes;
    totalCapacityMinutes += dayCapacity.availableMinutes;
    totalBookedMinutes += options?.includeOverbooking
      ? dayCapacity.rawBookedMinutes
      : dayCapacity.bookedMinutes;
  });

  const totalBookedHours = Math.round((totalBookedMinutes / 60) * 10) / 10;
  const totalCapacityHours = Math.round((totalCapacityMinutes / 60) * 10) / 10;
  const utilizationPercentage = totalCapacityHours > 0
    ? Math.min(999, Math.round((totalBookedHours / totalCapacityHours) * 100))
    : 0;

  return {
    totalBookedHours,
    totalCapacityHours,
    utilizationPercentage,
    totalWorkingHours: Math.round((totalWorkingMinutes / 60) * 10) / 10,
    totalLostHours: Math.round((totalLostMinutes / 60) * 10) / 10,
  };
}

export function calculatePeriodUtilization(
  resourceId: string,
  periodStart: Date,
  periodEnd: Date,
  bookings: Booking[],
  resource: Resource,
  globalWorkingProfile: WorkingHoursProfile,
  bankHolidays?: BankHoliday[],
  absences?: Absence[],
  bookingTypes?: BookingType[],
  options?: PeriodCapacityOptions
): PeriodUtilizationResult {
  const { totalBookedHours, totalCapacityHours, utilizationPercentage } = calculatePeriodCapacity(
    resourceId,
    periodStart,
    periodEnd,
    bookings,
    resource,
    globalWorkingProfile,
    bankHolidays,
    absences,
    bookingTypes,
    options
  );

  return { totalBookedHours, totalCapacityHours, utilizationPercentage };
}
